<template>
  <div :class="['annotation-toolbar', { 'annotation-toolbar--compact': isCompactMode }]">
    <!-- Compact Mode Toggle -->
    <div class="toolbar-section toolbar-section--compact-toggle">
      <button
        :class="['toolbar-btn', 'compact-toggle-btn', { active: isCompactMode }]"
        :title="isCompactMode ? $t('annotation.expandToolbar') || 'Expand Toolbar' : $t('annotation.compactToolbar') || 'Compact Mode'"
        @click="toggleCompactMode"
      >
        <i :class="isCompactMode ? 'fas fa-expand-arrows-alt' : 'fas fa-compress-arrows-alt'"></i>
      </button>
    </div>

    <!-- Drawing Tools (Grouped) -->
    <div class="toolbar-section toolbar-section--tools">
      <div v-if="!isCompactMode" class="toolbar-label">{{ $t('annotation.tools') }}</div>
      <div class="toolbar-buttons-groups">
        <div 
          v-for="(group, groupIndex) in toolGroups" 
          :key="group.id"
          :class="['toolbar-button-group', `toolbar-button-group--${group.id}`]"
        >
          <button
            v-for="tool in group.tools"
            :key="tool.id"
            :class="['toolbar-btn', { active: currentTool === tool.id }]"
            :title="`${$t(`annotation.${tool.id}`)} - ${group.label}`"
            @click="$emit('tool-change', tool.id)"
          >
            <i :class="tool.icon"></i>
          </button>
          <!-- Group separator -->
          <div 
            v-if="groupIndex < toolGroups.length - 1" 
            class="tool-group-separator"
          ></div>
        </div>
      </div>
    </div>

    <!-- Quick Color Indicator (opens context panel or color picker) -->
    <div class="toolbar-section color-picker-section" ref="colorPickerDropdown">
      <div v-if="!isCompactMode" class="toolbar-label">{{ $t('annotation.color') }}</div>
      <div class="toolbar-controls">
        <button
          type="button"
          class="color-picker-toggle"
          :title="$t('annotation.selectColor')"
          @click.stop="toggleColorPicker($event)"
        >
          <span class="color-picker-toggle__color" :style="{ backgroundColor: settings.strokeColor }"></span>
          <i class="fas fa-palette"></i>
        </button>
      </div>
      
      <!-- Dropdown Panel (using Teleport to render at body level) -->
      <Teleport to="body">
        <div 
          v-if="showColorPicker" 
          class="color-picker-panel annotation-color-picker-panel" 
          :style="dropdownPosition"
          data-testid="color-picker-panel"
        >
          <div class="color-picker-panel__header">
            <span>{{ $t('annotation.selectColor') || 'Select Color' }}</span>
            <button class="btn-close-picker" @click="showColorPicker = false" title="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <div class="color-picker-panel__content">
            <!-- Color Presets -->
            <div class="color-section">
              <div class="color-section__label">{{ $t('annotation.presetColors') || 'Preset Colors' }}</div>
              <div class="color-grid">
                <button
                  v-for="color in colorPresets"
                  :key="color"
                  :class="['color-swatch', { active: settings.strokeColor === color }]"
                  :style="{ backgroundColor: color }"
                  :title="getColorName(color)"
                  @click="selectColor(color)"
                >
                  <i v-if="settings.strokeColor === color" class="fas fa-check"></i>
                </button>
              </div>
            </div>
            
            <!-- Recent Colors -->
            <div v-if="recentColors.length > 0" class="color-section">
              <div class="color-section__label">{{ $t('annotation.recentColors') || 'Recent Colors' }}</div>
              <div class="color-grid">
                <button
                  v-for="(color, index) in recentColors"
                  :key="`recent-${index}`"
                  :class="['color-swatch', { active: settings.strokeColor === color }]"
                  :style="{ backgroundColor: color }"
                  :title="color"
                  @click="selectColor(color)"
                >
                  <i v-if="settings.strokeColor === color" class="fas fa-check"></i>
                </button>
              </div>
            </div>
            
            <!-- Custom Color Picker -->
            <div class="color-section">
              <div class="color-section__label">{{ $t('annotation.customColor') || 'Custom Color' }}</div>
              <div class="custom-color-input">
                <input
                  type="color"
                  :value="settings.strokeColor"
                  @input="handleColorChange"
                  class="color-input-native"
                />
                <input
                  type="text"
                  :value="settings.strokeColor"
                  @input="handleColorTextChange"
                  @blur="validateColorText"
                  class="color-input-text"
                  placeholder="#000000"
                  maxlength="7"
                />
              </div>
            </div>
          </div>
        </div>
      </Teleport>
    </div>

    <!-- History Controls -->
    <div class="toolbar-section">
      <div v-if="!isCompactMode" class="toolbar-label">{{ $t('annotation.history') }}</div>
      <div class="toolbar-buttons">
        <button
          :class="['toolbar-btn', { disabled: !canUndo }]"
          :title="$t('common.undo') + ' (Ctrl+Z)'"
          @click="$emit('undo')"
          :disabled="!canUndo"
        >
          <i class="fas fa-undo"></i>
        </button>
        <button
          :class="['toolbar-btn', { disabled: !canRedo }]"
          :title="$t('common.redo') + ' (Ctrl+Y)'"
          @click="$emit('redo')"
          :disabled="!canRedo"
        >
          <i class="fas fa-redo"></i>
        </button>
      </div>
    </div>

    <!-- Grid & Guides -->
    <div class="toolbar-section">
      <div v-if="!isCompactMode" class="toolbar-label">{{ $t('annotation.grid') }}</div>
      <div class="toolbar-grid-controls">
        <button
          :class="['toolbar-btn', { active: settings.showGrid }]"
          :title="$t('annotation.toggleGrid')"
          @click="$emit('setting-change', 'showGrid', !settings.showGrid)"
        >
          <i class="fas fa-th"></i>
        </button>
        <div v-if="settings.showGrid" class="toolbar-controls">
          <label class="grid-size-label">{{ $t('annotation.gridSize') }}:</label>
          <input
            type="number"
            min="10"
            max="100"
            step="5"
            :value="settings.gridSize || 20"
            @input="handleGridSizeChange"
            class="number-input number-input--small"
          />
          <span class="value-display">px</span>
        </div>
      </div>
    </div>

    <!-- Clear All -->
    <div class="toolbar-section">
      <div v-if="!isCompactMode" class="toolbar-label">{{ $t('annotation.clearAll') }}</div>
      <div class="toolbar-buttons">
        <button
          class="toolbar-btn toolbar-btn--danger"
          :title="$t('annotation.clearAll')"
          @click="$emit('clear-all')"
        >
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { AnnotationTool, type AnnotationToolSettings } from '@/types/annotation';

interface Props {
  currentTool: AnnotationTool;
  settings: AnnotationToolSettings;
  canUndo: boolean;
  canRedo: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'tool-change', tool: AnnotationTool): void;
  (e: 'setting-change', setting: string, value: any): void;
  (e: 'undo'): void;
  (e: 'redo'): void;
  (e: 'clear-all'): void;
  (e: 'calibrate-scale'): void;
}>();

const { t } = useI18n();

// Compact mode state
const isCompactMode = ref(false);

// Tool definitions grouped by function
const toolGroups = [
  {
    id: 'selection',
    label: 'Selection',
    tools: [
      { id: AnnotationTool.SELECT, icon: 'fas fa-mouse-pointer' },
    ]
  },
  {
    id: 'drawing',
    label: 'Drawing',
    tools: [
      { id: AnnotationTool.FREEHAND, icon: 'fas fa-pencil-alt' },
      { id: AnnotationTool.RECTANGLE, icon: 'fas fa-square' },
      { id: AnnotationTool.CIRCLE, icon: 'fas fa-circle' },
      { id: AnnotationTool.ARROW, icon: 'fas fa-arrow-right' },
      { id: AnnotationTool.LINE, icon: 'fas fa-minus' },
    ]
  },
  {
    id: 'annotation',
    label: 'Annotation',
    tools: [
      { id: AnnotationTool.TEXT, icon: 'fas fa-font' },
      { id: AnnotationTool.HIGHLIGHT, icon: 'fas fa-highlighter' },
    ]
  },
  {
    id: 'measurement',
    label: 'Measurement',
    tools: [
      { id: AnnotationTool.MEASURE_DISTANCE, icon: 'fas fa-ruler' },
      { id: AnnotationTool.MEASURE_AREA, icon: 'fas fa-draw-polygon' },
      { id: AnnotationTool.MEASURE_POLYGON, icon: 'fas fa-shapes' },
    ]
  },
  {
    id: 'utility',
    label: 'Utility',
    tools: [
      { id: AnnotationTool.ERASER, icon: 'fas fa-eraser' },
    ]
  }
];

// Legacy flat tools array for backward compatibility
const tools = toolGroups.flatMap(group => group.tools);

// Color presets
const colorPresets = [
  '#FF0000', // Red
  '#FF6B00', // Orange
  '#FFFF00', // Yellow
  '#00FF00', // Green
  '#00FFFF', // Cyan
  '#0000FF', // Blue
  '#FF00FF', // Magenta
  '#8B00FF', // Purple
  '#000000', // Black
  '#808080', // Gray
  '#FFFFFF', // White
  '#964B00', // Brown
];

// Color names for tooltips
const colorNames: Record<string, string> = {
  '#FF0000': 'Red',
  '#FF6B00': 'Orange',
  '#FFFF00': 'Yellow',
  '#00FF00': 'Green',
  '#00FFFF': 'Cyan',
  '#0000FF': 'Blue',
  '#FF00FF': 'Magenta',
  '#8B00FF': 'Purple',
  '#000000': 'Black',
  '#808080': 'Gray',
  '#FFFFFF': 'White',
  '#964B00': 'Brown',
};

// Recent colors (stored in ref to track usage)
const recentColors = ref<string[]>([]);

// Color picker dropdown state
const showColorPicker = ref(false);
const colorPickerDropdown = ref<HTMLElement | null>(null);

// Computed dropdown position (for teleported dropdown)
const dropdownPosition = computed(() => {
  if (!colorPickerDropdown.value) {
    console.warn('Dropdown ref not available');
    return {
      position: 'fixed',
      top: '100px',
      left: '100px',
    };
  }
  
  try {
    const rect = colorPickerDropdown.value.getBoundingClientRect();
    const position = {
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
    };
    console.log('Dropdown position calculated:', position);
    return position;
  } catch (error) {
    console.error('Error calculating dropdown position:', error);
    return {
      position: 'fixed',
      top: '100px',
      left: '100px',
    };
  }
});

// Get color name
const getColorName = (color: string): string => {
  return colorNames[color.toUpperCase()] || color;
};

// Add color to recent colors
const addToRecentColors = (color: string) => {
  // Remove if already exists
  const index = recentColors.value.indexOf(color);
  if (index > -1) {
    recentColors.value.splice(index, 1);
  }
  // Add to beginning
  recentColors.value.unshift(color);
  // Keep only last 6 colors
  if (recentColors.value.length > 6) {
    recentColors.value.pop();
  }
  // Save to localStorage
  try {
    localStorage.setItem('annotation-recent-colors', JSON.stringify(recentColors.value));
  } catch (e) {
    console.warn('Failed to save recent colors:', e);
  }
};

// Load recent colors from localStorage
const loadRecentColors = () => {
  try {
    const saved = localStorage.getItem('annotation-recent-colors');
    if (saved) {
      recentColors.value = JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Failed to load recent colors:', e);
  }
};

// Load compact mode preference
const loadCompactMode = () => {
  try {
    const saved = localStorage.getItem('annotation-compact-mode');
    if (saved !== null) {
      isCompactMode.value = JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Failed to load compact mode:', e);
  }
};

// Toggle compact mode
const toggleCompactMode = () => {
  isCompactMode.value = !isCompactMode.value;
  try {
    localStorage.setItem('annotation-compact-mode', JSON.stringify(isCompactMode.value));
  } catch (e) {
    console.warn('Failed to save compact mode:', e);
  }
};

// Toggle color picker dropdown
const toggleColorPicker = (event?: Event) => {
  if (event) {
    event.stopPropagation();
  }
  showColorPicker.value = !showColorPicker.value;
  console.log('Color picker toggled:', showColorPicker.value);
  console.log('Dropdown element:', colorPickerDropdown.value);
  
  if (showColorPicker.value) {
    nextTick(() => {
      const panel = document.querySelector('.color-picker-panel');
      console.log('Panel found:', panel);
      if (panel) {
        console.log('Panel styles:', window.getComputedStyle(panel).position, window.getComputedStyle(panel).zIndex);
      }
    });
  }
};

// Select a color
const selectColor = (color: string) => {
  addToRecentColors(color);
  emit('setting-change', 'strokeColor', color);
  showColorPicker.value = false;
};

// Handle click outside to close dropdown
const handleClickOutside = (e: MouseEvent) => {
  if (!showColorPicker.value) return;
  
  const target = e.target as Node;
  const panel = document.querySelector('.color-picker-panel');
  
  // Check if click is outside both the toggle button and the panel
  const isOutsideToggle = colorPickerDropdown.value && !colorPickerDropdown.value.contains(target);
  const isOutsidePanel = panel && !panel.contains(target);
  
  if (isOutsideToggle && isOutsidePanel) {
    console.log('Clicked outside, closing picker');
    showColorPicker.value = false;
  }
};

// Initialize
onMounted(() => {
  console.log('AnnotationToolbar mounted');
  console.log('Color picker dropdown ref:', colorPickerDropdown.value);
  loadRecentColors();
  loadCompactMode();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Event handlers
const handleColorChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const color = target.value.toUpperCase();
  addToRecentColors(color);
  emit('setting-change', 'strokeColor', color);
};

const handleColorTextChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  let color = target.value.trim();
  
  // Add # if missing
  if (color && !color.startsWith('#')) {
    color = '#' + color;
  }
  
  // Validate hex color format
  if (/^#[0-9A-F]{6}$/i.test(color)) {
    addToRecentColors(color.toUpperCase());
    emit('setting-change', 'strokeColor', color.toUpperCase());
  }
};

const validateColorText = (e: Event) => {
  const target = e.target as HTMLInputElement;
  let color = target.value.trim();
  
  // Add # if missing
  if (color && !color.startsWith('#')) {
    color = '#' + color;
  }
  
  // If invalid, revert to current color
  if (!/^#[0-9A-F]{6}$/i.test(color)) {
    target.value = props.settings.strokeColor;
  }
};

const handleStrokeWidthChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit('setting-change', 'strokeWidth', parseInt(target.value));
};

const handleFontSizeChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit('setting-change', 'fontSize', parseInt(target.value));
};

// Get current scale value (the number after "1:")
const getCurrentScaleValue = (): number => {
  const currentScale = props.settings.scale || '1:100';
  const parts = currentScale.split(':');
  return parseFloat(parts[1]) || 100;
};

// Handle custom scale input (real-time updates)
const handleCustomScaleInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = parseFloat(target.value);
  
  if (value && value > 0) {
    const scaleLabel = `1:${value}`;
    emit('setting-change', 'scale', scaleLabel);
    emit('setting-change', 'scaleRatio', value);
  }
};

// Handle custom scale blur/enter (validation)
const handleCustomScaleBlur = (e: Event) => {
  const target = e.target as HTMLInputElement;
  let value = parseFloat(target.value);
  
  // Validate and ensure minimum value
  if (!value || value < 1) {
    value = 100; // Default fallback
    target.value = '100';
  }
  
  const scaleLabel = `1:${value}`;
  emit('setting-change', 'scale', scaleLabel);
  emit('setting-change', 'scaleRatio', value);
};

const handleUnitChange = (e: Event) => {
  const target = e.target as HTMLSelectElement;
  emit('setting-change', 'measurementUnit', target.value);
};

const handleOpacityChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const opacity = parseInt(target.value) / 100;
  emit('setting-change', 'opacity', opacity);
};

const handleGridSizeChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit('setting-change', 'gridSize', parseInt(target.value));
};
</script>

<style scoped>
.annotation-toolbar {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 12px 16px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
  overflow-y: visible;
  flex-wrap: nowrap;
  position: relative;
  z-index: 50;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.annotation-toolbar--compact {
  padding: 8px 16px;
  gap: 16px;
}

/* Compact mode toggle button */
.toolbar-section--compact-toggle {
  border-right: 2px solid #e5e7eb;
  padding-right: 16px;
  margin-right: 0;
}

.compact-toggle-btn {
  background: #f3f4f6;
  transition: all 0.2s;
  position: relative;
}

.compact-toggle-btn::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 2px;
  background: #3b82f6;
  transition: width 0.3s ease;
}

.compact-toggle-btn:hover {
  background: #e0e7ff;
  color: #3b82f6;
  transform: translateY(-1px);
}

.compact-toggle-btn.active {
  background: #3b82f6;
  color: white;
}

.compact-toggle-btn.active::after {
  width: 100%;
}

.toolbar-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.annotation-toolbar--compact .toolbar-section {
  gap: 0;
}

.toolbar-section--tools {
  flex: 0 0 auto;
}

.toolbar-section:not(.toolbar-section--compact-toggle):not(.toolbar-section--tools) {
  border-left: 1px solid #e5e7eb;
  padding-left: 16px;
}

.toolbar-label {
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  transition: opacity 0.3s ease;
  margin-bottom: 4px;
  white-space: nowrap;
}

.annotation-toolbar--compact .toolbar-label {
  display: none;
}

.toolbar-buttons {
  display: flex;
  gap: var(--space-xs);
}

/* Tool groups container */
.toolbar-buttons-groups {
  display: flex;
  align-items: center;
  gap: 0;
  flex-wrap: wrap;
}

/* Individual tool group */
.toolbar-button-group {
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  padding: 6px 12px;
  border-right: 1px solid #e5e7eb;
  background: #fafafa;
  border-radius: 6px;
  margin-right: 8px;
}

.toolbar-button-group:last-child {
  border-right: none;
  margin-right: 0;
}

.toolbar-button-group:first-child {
  margin-left: 0;
}

.toolbar-button-group:hover {
  background: #f3f4f6;
}

/* Group hover effect for selection */
.toolbar-button-group--selection {
  background: #fef3f2;
  border-color: #fee2e2;
}

.toolbar-button-group--drawing {
  background: #f0fdf4;
  border-color: #dcfce7;
}

.toolbar-button-group--annotation {
  background: #fef9c3;
  border-color: #fef08a;
}

.toolbar-button-group--measurement {
  background: #eff6ff;
  border-color: #dbeafe;
}

.toolbar-button-group--utility {
  background: #faf5ff;
  border-color: #f3e8ff;
}

/* Group separator (hidden, using border instead) */
.tool-group-separator {
  display: none;
}

/* Group-specific styling (for future customization) */

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  height: 40px;
  font-size: 14px;
  color: #6b7280;
  position: relative;
}

.annotation-toolbar--compact .toolbar-btn {
  min-width: 34px;
  height: 34px;
  padding: 6px;
  font-size: 13px;
}

.toolbar-btn:hover:not(:disabled) {
  background: #f3f4f6;
  color: #3b82f6;
  transform: translateY(-1px);
  border-color: #e5e7eb;
}

.toolbar-btn.active {
  background: #eff6ff;
  color: #3b82f6;
  border-color: #3b82f6;
  font-weight: 500;
}

.toolbar-btn.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 2px;
  background: #3b82f6;
  border-radius: 2px 2px 0 0;
}

.toolbar-btn:disabled,
.toolbar-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toolbar-btn--danger {
  color: var(--danger);
}

.toolbar-btn--danger:hover:not(:disabled) {
  background: var(--danger);
  color: white;
  border-color: var(--danger);
}

.toolbar-btn--primary {
  background: var(--primary-light);
  color: var(--primary);
  border-color: var(--primary);
}

.toolbar-btn--primary:hover:not(:disabled) {
  background: var(--primary);
  color: white;
}

.toolbar-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

/* Grid Controls - Horizontal Layout */
.toolbar-grid-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
}

/* Color Picker Dropdown */
.color-picker-section {
  position: relative;
  z-index: 100;
}

.color-picker-dropdown {
  position: relative;
}

.color-picker-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 6px 10px;
  min-width: 80px;
  height: 36px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.annotation-toolbar--compact .color-picker-toggle {
  min-width: 70px;
  height: 32px;
  padding: 4px 8px;
}

.color-picker-toggle:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.color-picker-toggle__color {
  flex: 1;
  height: 24px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.annotation-toolbar--compact .color-picker-toggle__color {
  height: 20px;
}

.color-picker-toggle i {
  font-size: 12px;
  color: var(--text-secondary);
}

/* Note: All .color-picker-panel styles moved to unscoped section below for Teleport compatibility */

.slider {
  width: 100px;
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
  border: none;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  transition: all 0.2s;
}

.annotation-toolbar--compact .slider {
  width: 80px;
  height: 4px;
}

.slider:hover {
  background: #d1d5db;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #3b82f6;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.2s;
}

.slider::-webkit-slider-thumb:hover {
  transform: scale(1.15);
  box-shadow: 0 2px 6px rgba(59, 130, 246, 0.4);
  background: #2563eb;
}

.slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #3b82f6;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.2s;
}

.slider::-moz-range-thumb:hover {
  transform: scale(1.15);
  box-shadow: 0 2px 6px rgba(59, 130, 246, 0.4);
  background: #2563eb;
}

.value-display {
  font-size: 12px;
  font-weight: 500;
  min-width: 40px;
  text-align: left;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

.annotation-toolbar--compact .value-display {
  font-size: 11px;
  min-width: 35px;
}

.number-input {
  width: 70px;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius);
  font-size: 12px !important;
}

.scale-select,
.unit-select {
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  background: white;
  cursor: pointer;
}

.scale-select {
  width: 80px;
}

.unit-select {
  width: 90px;
}

.scale-select:focus,
.unit-select:focus {
  outline: none;
  border-color: var(--primary);
}

/* Custom scale input group */
.scale-input-group {
  display: flex;
  align-items: center;
  gap: 4px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 10px;
  transition: all 0.2s;
  height: 36px;
  min-width: 90px;
}

.annotation-toolbar--compact .scale-input-group {
  height: 32px;
  padding: 4px 8px;
  min-width: 80px;
}

.scale-input-group:hover {
  border-color: #3b82f6;
  box-shadow: 0 1px 3px rgba(59, 130, 246, 0.1);
}

.scale-input-group:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.scale-prefix {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  line-height: 1;
}

.annotation-toolbar--compact .scale-prefix {
  font-size: 12px;
}

.scale-input {
  width: 55px;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  outline: none;
  line-height: 1;
  color: #1f2937;
}

.annotation-toolbar--compact .scale-input {
  width: 50px;
  font-size: 12px;
}

.scale-input::-webkit-inner-spin-button,
.scale-input::-webkit-outer-spin-button {
  opacity: 0.7;
  height: 16px;
  width: 14px;
  cursor: pointer;
}

.scale-input::-webkit-inner-spin-button:hover,
.scale-input::-webkit-outer-spin-button:hover {
  opacity: 1;
}

/* Grid controls */
.grid-size-label {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  white-space: nowrap;
}

.number-input--small {
  width: 60px;
}

/* Responsive */
@media (max-width: 1200px) {
  .annotation-toolbar {
    flex-wrap: wrap;
  }
  
  .toolbar-section--tools {
    flex: 0 0 100%;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
  }
}

@media (max-width: 768px) {
  .annotation-toolbar {
    gap: 12px;
    padding: 8px 12px;
  }

  .toolbar-button-group {
    padding: 0 8px;
  }

  .slider {
    width: 80px;
  }

  .toolbar-section:not(.toolbar-section--compact-toggle):not(.toolbar-section--tools) {
    padding-left: 12px;
  }
}
</style>

<style>
/* Unscoped styles for teleported color picker - uses attribute selector to work with any Vue scoped ID */
div[data-testid="color-picker-panel"] {
  /* Position and layering */
  position: fixed !important;
  z-index: 100001 !important; /* Higher than context panel (100000) */
  
  /* Size constraints */
  min-width: 320px !important;
  max-width: 400px !important;
  width: 320px !important;
  height: auto !important;
  
  /* Visual styling */
  background-color: #ffffff !important;
  background: white !important;
  border: 2px solid #333333 !important;
  border-radius: 8px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
  
  /* Display properties */
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  overflow: visible !important;
  
  /* Interaction */
  pointer-events: auto !important;
  
  /* Reset any canvas/fabric interference */
  transform: none !important;
  filter: none !important;
  mix-blend-mode: normal !important;
  isolation: isolate !important;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Reset all children to prevent inherited styles */
div[data-testid="color-picker-panel"] * {
  box-sizing: border-box !important;
}

/* Use descendant selectors from the panel */
div[data-testid="color-picker-panel"] .color-picker-panel__header {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  background-color: #f5f5f5 !important;
  border-radius: 8px 8px 0 0 !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

div[data-testid="color-picker-panel"] .color-picker-panel__header span {
  font-weight: 600 !important;
  color: #1a1a1a !important;
  font-size: 15px !important;
  letter-spacing: 0.3px !important;
}

div[data-testid="color-picker-panel"] .btn-close-picker {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 24px !important;
  height: 24px !important;
  background: transparent !important;
  border: none !important;
  border-radius: 4px !important;
  cursor: pointer !important;
  color: #666 !important;
  transition: all 0.2s !important;
}

div[data-testid="color-picker-panel"] .btn-close-picker:hover {
  background: #e0e0e0 !important;
  color: #333 !important;
}

div[data-testid="color-picker-panel"] .color-picker-panel__content {
  padding: 16px !important;
  max-height: 400px !important;
  overflow-y: auto !important;
  background-color: #ffffff !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

div[data-testid="color-picker-panel"] .color-section {
  margin-bottom: 16px !important;
}

div[data-testid="color-picker-panel"] .color-section:last-child {
  margin-bottom: 0 !important;
}

div[data-testid="color-picker-panel"] .color-section__label {
  font-size: 11px !important;
  font-weight: 700 !important;
  color: #555 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.8px !important;
  margin-bottom: 10px !important;
  display: block !important;
}

div[data-testid="color-picker-panel"] .color-grid {
  display: grid !important;
  grid-template-columns: repeat(6, 1fr) !important;
  gap: 10px !important;
  padding: 2px !important;
}

div[data-testid="color-picker-panel"] .color-swatch {
  position: relative !important;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  min-height: 40px !important;
  max-width: 40px !important;
  max-height: 40px !important;
  border: 2px solid #e0e0e0 !important;
  border-radius: 8px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  /* Don't override background-color - let inline styles work */
}

div[data-testid="color-picker-panel"] .color-swatch:hover {
  transform: scale(1.15) !important;
  border-color: #3b82f6 !important;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.25) !important;
  z-index: 10 !important;
}

div[data-testid="color-picker-panel"] .color-swatch.active {
  border-color: #3b82f6 !important;
  border-width: 3px !important;
  box-shadow: 0 0 0 2px #3b82f6 !important;
}

div[data-testid="color-picker-panel"] .color-swatch i {
  color: white !important;
  font-size: 16px !important;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8), 0 0 2px rgba(0, 0, 0, 1) !important;
  font-weight: bold !important;
  line-height: 1 !important;
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
}

div[data-testid="color-picker-panel"] .custom-color-input {
  display: flex !important;
  gap: 8px !important;
  align-items: center !important;
}

div[data-testid="color-picker-panel"] .color-input-native {
  width: 60px !important;
  height: 40px !important;
  border: 2px solid #d0d0d0 !important;
  border-radius: 6px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
  overflow: hidden !important;
}

div[data-testid="color-picker-panel"] .color-input-native:hover {
  border-color: #3b82f6 !important;
  box-shadow: 0 2px 6px rgba(59, 130, 246, 0.2) !important;
}

div[data-testid="color-picker-panel"] .color-input-text {
  flex: 1 !important;
  padding: 10px 14px !important;
  border: 2px solid #d0d0d0 !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  font-family: 'Courier New', monospace !important;
  text-transform: uppercase !important;
  transition: all 0.2s !important;
  background: white !important;
  color: #333 !important;
}

div[data-testid="color-picker-panel"] .color-input-text:focus {
  outline: none !important;
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  div[data-testid="color-picker-panel"].color-picker-panel {
    min-width: 280px !important;
  }

  div[data-testid="color-picker-panel"] .color-grid {
    grid-template-columns: repeat(4, 1fr) !important;
  }
}

/* High specificity override to ensure visibility */
div[data-testid="color-picker-panel"].annotation-color-picker-panel.color-picker-panel {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  pointer-events: auto !important;
}
</style>
